import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, formatNumber, getRemotedbUrl, isNotEmpty } from '../../../lib/ep.js';
import { useCSS } from '../../../lib/hooks.js';
import { stage_promotion_types, with_score} from '../../../data/bootstrap_stages';

import { SquaredButton } from '../../builder/ActionButton';
import ButtonCollection from '../../builder/ButtonCollection.js';
import { DeleteButton } from '../../builder/ConfirmButton';
import { DisplayDynamicForm } from '../../builder/DynamicForm.js';
import FieldLabel from '../../builder/FieldLabel';
import FloatingLayer from '../../builder/FloatingLayer';
import SectionSubtitle from '../../builder/SectionSubtitle';
import SectionTitle from '../../builder/SectionTitle';
import Tooltip from '../../builder/Tooltip';

import SingleGallery from '../../front/SingleGallery';
import QuizStatsPublic from '../../front/quiz/QuizStatsPublic';

import DeleteUserConfirm, { delMedia } from '../../media/DeleteUserConfirm.js';

import MultiEntriesRanking from '../../users/MultiEntriesRanking';
import LeveledHTMLText from '../helpers/LeveledHTMLText.js';
import { UserTeamParticipationDetail } from '../../../containers/TeamClient.js';
import ParticipationStatus from '../helpers/ParticipationStatus.js';
import { useAccount } from '../../context/accountContext.js';
import RibbonCollection from '../../ui_items/RibbonCollection.js';
import { RequirementsOCRSimple } from '../../backend/PromotionRequirementsTool.js';
import { GuessScoreResumeWithPoints } from '../GuessScoreResume.js';
import OneTimeBracketFinal from '../OneTimeBracketFinal.js';

const local_tz = 'Europe/Madrid';

const formatStageName = (stage, intl) => {
	if(isNotEmpty(stage, 'display_title')) return stage.display_title;
	
	const i18n = (m, c) => i18nIntl(m, intl, c);

	return `${i18n("Stage")}${(stage?.order ?? false) ? ` #${stage.order}` : ""}`;
};

const formatDate = (obj, tz) => {
	return `${EPGetTimezone(obj.created, tz, 'LL')}, ${EPGetTimezone(obj.created, tz, 'LT')}`;
};

const getCompletenessData = (data) => {
	const has_completed_enabled = (data.completed != undefined) ? true : false;

	if(has_completed_enabled === false) {
		return {
			compl_enabled:false,
			compl_color:'transparent',
			compl_tooltip:"",
		}
	}

	const stage_completeness_status = data?.completed ?? false;

	return {
		compl_enabled:true,
		compl_color:(stage_completeness_status) ? 'green' : 'red',
		compl_tooltip:(stage_completeness_status) ? "Completed" : "Not completed",
	}
};

const ParticipationDetailLayer = ({ participation, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = participation.Stage;
	const stage_score = participation.StageScore;
	const in_scores = props?.in_scores ?? false;
	const type = props?.type ?? 'single'; // 'single' | 'grouped_by_stage'
	const tab_scores = in_scores ? true : false;

	const { compl_enabled, compl_color, compl_tooltip } = getCompletenessData((type == 'single') ? participation.Participation : participation.StageScore);

	const has_quiz_stages = (!stage.is_game && !stage.is_new_game && !tab_scores) ? true : false;
	const has_dynamic_form = (stage_score.quiz_entry?.form != undefined) ? true : false;
	const has_extra_contents = (has_quiz_stages || has_dynamic_form) ? true : false;

	return (
		<FloatingLayer _class={{size:'small'}} backButton={props.backButton}>
			<div class="without_actions">
				<SectionTitle>Participation detail</SectionTitle>

				<SectionSubtitle
					autotranslate={false}
				>{formatDate(stage_score, props.tz)}</SectionSubtitle>

				{((stage.is_game || stage.is_quiz)) && (
					<LeveledHTMLText
						_class={{ other:'gui_participations_list' }}
					>
						<section class="gui_quiz_public_stats" style={(has_extra_contents) ? { marginBottom:'2em' } : null}>
							<article class="gui_flexbox_auto gui_flex_col gui_flex_align_top">
								{(stage_score.time != undefined) && (
									<p style={{marginBottom:'0.2em'}}>
										{i18n("Played time")}:
										<strong>&nbsp;{stage_score.time} {i18n("sec")}</strong>
									</p>
								)}

								{(stage_score.score != undefined) && (
									<p>
										{i18n("Score")}:
										<strong>&nbsp;{formatNumber(stage_score.score, 2)} {i18n("points")}</strong>
									</p>
								)}

								{(compl_enabled !== false) && (
									<RibbonCollection
										items={[{
											title:compl_tooltip,
											color:compl_color,
										}]}
										_class={{ other:'gui_not_in_top' }}
									/>
								)}
							</article>
						</section>
					</LeveledHTMLText>
				)}

				{(has_quiz_stages) && (
					<QuizStatsPublic
						is_builder={true}
						promotion_id={promotion.id}
						user_id={stage_score.id}
						stage_id={stage_score.stage_id}
					/>
				)}

				{(has_dynamic_form) && (
					<DisplayDynamicForm
						form={props.stages.find((st) => (st.Stage.id == stage_score.stage_id)).Stage.form}
						values={stage_score.quiz_entry?.form}
						is_builder={true}
					/>
				)}
			</div>
		</FloatingLayer>
	)
};

const FaceOffDetail = ({ participation, stages, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = stages.find((s) => (participation.stage == s.Stage.id));

	if(stage == undefined) {
		return (
			<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
				<div class="without_actions">
					<SectionTitle>Participation detail</SectionTitle>

					<div>{i18n("Empty stage data.")}</div>
				</div>
			</FloatingLayer>
		)
	}

	const faceoff_data = participation?.quiz_entry?.faceoff ?? {};

	const players = stage.Stage?.faceoff_players ?? [];
	const winner = players.find((pl) => (faceoff_data.winner == pl.id));

	return (
		<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
			<div class="without_actions">
				<SectionTitle>Participation detail</SectionTitle>

				<div>{winner?.name ?? i18n("Unknown option")}</div>
			</div>
		</FloatingLayer>
	)
};

const OneTimeBracketDetail = ({ participation, stages, promotion,  ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = stages.find((s) => (participation.stage == s.Stage.id));

	if(stage == undefined) {
		return (
			<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
				<div class="without_actions">
					<SectionTitle>Participation detail</SectionTitle>

					<div>{i18n("Empty stage data.")}</div>
				</div>
			</FloatingLayer>
		)
	}

	const guess_score_data = participation?.quiz_entry?.guess_score ?? {};
	
	const teams = promotion?.guess_score_teams ?? [];
	const matches = stage.Stage?.guess_score_matches ?? [];
	const results = stage.Stage?.guess_score_matches_result ?? {};
	return (
		<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
			<div class="without_actions">
				<SectionTitle>Participation detail</SectionTitle>

				<SectionSubtitle
					autotranslate={false}
				>{formatDate(participation, promotion.timezone)}</SectionSubtitle>

				{(participation.quiz_result != undefined) && (
					<p>
						{i18n("Score")}:
						<strong>&nbsp;{formatNumber(participation.quiz_result,2)} {i18n("points")}</strong>
					</p>
				)}

				<div class="in_user_list gui_game_wrapper">
					<OneTimeBracketFinal
						stage={stage.Stage}
						teams={teams}
						promotion={promotion}
						matches={matches}
						results={results}
						answers={guess_score_data}
						intl={props.intl}
						is_standalone={true}
						only_contents={true}
						enable_actions={false}
					/>
				</div>
			</div>
		</FloatingLayer>
	)
};


const LikeDislikeDetail = ({ participation, stages, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = stages.find((s) => (participation.stage == s.Stage.id));

	if(stage == undefined) {
		return (
			<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
				<div class="without_actions">
					<SectionTitle>Participation detail</SectionTitle>

					<div>{i18n("Empty stage data.")}</div>
				</div>
			</FloatingLayer>
		)
	}

	const likedislike_data = (participation?.quiz_entry?.likedislike ?? []);
	const players = stage.Stage?.likedislike_players ?? [];

	const likes = [];
	likedislike_data.filter((p) => (p.type == 'like')).map((pl) => {
		const player = players.find((o) => (o.id == pl.id));
		if(player != undefined && isNotEmpty(player, 'name')) {
			likes.push({
				title:player.name,
				color:'green',
			});
		}
	});

	const dislikes = [];
	likedislike_data.filter((p) => (p.type == 'dislike')).map((pl) => {
		const player = players.find((o) => (o.id == pl.id));
		if(player != undefined && isNotEmpty(player, 'name')) {
			dislikes.push({
				title:player.name,
				color:'red',
			});
		}
	});

	return (
		<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
			<div class="without_actions">
				<SectionTitle>Participation detail</SectionTitle>

				<div>
					<strong class="gui_not_in_end_small">{i18n("Likes")}:</strong>

					<RibbonCollection
						items={likes}
						sort_ribbons={true}
						paint_if_empty={true}
					/>
				</div>

				<div class="gui_not_in_top">
					<strong class="gui_not_in_end_small">{i18n("***Dislikes")}:</strong>

					<RibbonCollection
						items={dislikes}
						sort_ribbons={true}
						paint_if_empty={true}
					/>
				</div>
			</div>
		</FloatingLayer>
	)
};

const GuessScoreDetail = ({ participation, promotion, stages, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const stage = stages.find((s) => (participation.stage == s.Stage.id));
	
	if(stage == undefined) {
		return (
			<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
				<div class="without_actions">
					<SectionTitle>Participation detail</SectionTitle>

					<div>{i18n("Empty stage data.")}</div>
				</div>
			</FloatingLayer>
		)
	}

	const guess_score_data = participation?.quiz_entry?.guess_score ?? {};
	
	const teams = promotion?.guess_score_teams ?? [];
	const matches = stage.Stage?.guess_score_matches ?? [];
	const results = stage.Stage?.guess_score_matches_result ?? {};

	return (
		<FloatingLayer _class={{ size:'small' }} backButton={props.backButton}>
			<div class="without_actions">
				<SectionTitle>Participation detail</SectionTitle>

				<SectionSubtitle
					autotranslate={false}
				>{formatDate(participation, promotion.timezone)}</SectionSubtitle>
				
				<GuessScoreResumeWithPoints
					stage={stage.Stage}
					teams={teams}
					matches={matches}
					results={results}
					answers={guess_score_data}
					intl={props.intl}
				/>
			</div>
		</FloatingLayer>
	)
};

const SingleParticipation = ({ part, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const { MyAccount } = useAccount();

	const [ display_ocr_detail, setDisplayOCRDetail ] = useState(false);

	const total_participations = props?.total_participations ?? 10;
	const counter = total_participations - (props?.i ?? 0);

	const stage = part.Stage;
	const stage_score = part.StageScore;

	const type = props?.type ?? 'single'; // 'single' | 'grouped_by_stage'

	let has_stage_custom_fields = false;
	if((part?.StageParticipation ?? []).length > 0) {
		part.StageParticipation.map((sp) => {
			if((sp?.config?.mediatype ?? 'text') == 'image') has_stage_custom_fields = true;
		});
	}

	const qz = stage_score?.quiz_entry ?? {};
	const display_details = props?.display_details ?? false;
	const display_prizes = props?.display_prizes ?? false;
	const display_date = props?.display_date ?? false;
	const display_origin = props?.display_origin ?? false;
	const display_complete_info = props?.display_complete_info ?? false;
	const display_code = (part.StageScore?.code ?? false) && !stage.is_dynamic_qr_req;
	const show_media = (qz.media != undefined) ? true : false;
	const show_faceoff = (qz.faceoff != undefined) ? true : false;
	const display_view = ['2', '3'].includes(stage_score.public_entry) ? false : true;	// false if "processing" (2) or "error" (3) 
	const has_score = props?.has_score ?? false;
	const in_scores = props?.in_scores ?? false;
	const has_ranking_enabled = props?.has_ranking_enabled ?? false;

	const { compl_enabled, compl_color, compl_tooltip } = getCompletenessData((type == 'single') ? part.Participation : part.StageScore);
	
	return (
		<tr>
			<td class="ranking_pos">#{(total_participations > 10 && counter < 10) ? `0${counter}` : counter}</td>

			<td class="gui_text_align_start">
				<div class="sided_contents">
					{(display_complete_info && (stage?.icon ?? null)) && (
						<Tooltip
							position="top"
							icon={stage.icon}
						>{stage.stage_type}</Tooltip>
					)}

					<div class="title is_multilined">
						<span class="gui_cutTextOnOverflow">
							{formatStageName(stage, props.intl)}

							{(display_code) && (
								<em>{i18n("Code")}: {stage_score.code}</em>
							)}
						</span>
					</div>
				</div>
			</td>

			{(display_date) && (
				<td class="gui_text_align_start">
					{EPGetTimezone(stage_score[(!in_scores) ? 'created' : 'modified'], props.tz, MyAccount.getAgentDateTimeFormat())}
				</td>
			)}

			{(display_origin) && (
				<td class="gui_text_align_start">
					<div class="sided_contents">
						{(isNotEmpty(stage_score, 'ip')) && (
							<span style={{flexGrow:'1'}}>{stage_score.ip}</span>
						)}

						{(isNotEmpty(stage_score, 'device')) && (
							<Tooltip position="top" icon={`knk-${stage_score.device}`}>{stage_score.device_label}</Tooltip>
						)}

						{(isNotEmpty(stage_score, 'browser')) && (
							<Tooltip position="top" icon={`knk-${stage_score.browser}`}>{stage_score.browser_label}</Tooltip>
						)}
					</div>
				</td>
			)}

			{(has_ranking_enabled || has_score) && (
				<td class="gui_text_align_end">
					<span class="gui_flexbox_auto gui_flex_end gui_flex_align_center">
						<RibbonCollection
							items={[{
								title:formatNumber(stage_score.score),
								color:compl_color,
								tooltip:(compl_enabled !== false) ? <Tooltip
									with_icon={false}
									position='top'
								>{compl_tooltip}</Tooltip> : null,
							}]}
						/>
					</span>
				</td>
			)}

			<td>
				<ButtonCollection _class={{other:'gui_flex_end'}}>
					{(has_stage_custom_fields) && (
						<SquaredButton
							icon='knk-information'
							onClick={() => props.displayAdditionalInformation(part)}
							tooltip={<Tooltip with_icon={false} position="top">Additional fields</Tooltip>}
							aria-label="View additional information"
						/>
					)}

					<ParticipationStatus
						status={stage_score.public_entry}
					/>

					{(display_prizes && (stage_score?.has_prize ?? false)) && (
						<SquaredButton
							icon='knk-winners'
							tooltip={<Tooltip with_icon={false} position="top">Won a prize</Tooltip>}
							aria-label="User won a prize"
						/>
					)}

					{(stage_score?.has_ticket ?? false) && (
						<SquaredButton
							icon='knk-buy-ticket'
							onClick={() => setDisplayOCRDetail(stage_score.has_ticket)}
							tooltip={<Tooltip with_icon={false} position="top">Validated a ticket</Tooltip>}
							aria-label="User validated a ticket"
						/>
					)}

					{((display_details || show_media || show_faceoff || stage_score?.quiz_entry?.form != undefined) && display_view) ? (
						<SquaredButton
							onClick={() => props.displayDetails(part, show_media)}
							icon='knk-visible'
							aria-label="Display details"
						/>
					) : (
						<SquaredButton
							icon='knk-white-space'
							_class={{ other:'gui_withoutEvents disabledItem' }}
							style={{ opacity:'0' }}
						/>
					)}

					{((display_details || show_media || show_faceoff) && !display_view) && (
						<SectionSubtitle
							squared={true}
							icon='knk-visible'
							_class={{ other:'gui_withoutEvents disabledItem' }}
							aria-label="Display details"
						/>
					)}

					{(!(stage?.is_media ?? false) && !(stage?.is_voting ?? false)) && (
						<DeleteButton
							squared={true}
							onClick={(e, cb) => props.actions({ type:'delete_participation', id:stage_score.id })}
							_position="left"
							aria-label="Delete participation"
						/>
					)}

					{(stage?.is_media ?? false) && (
						<DeleteButton
							squared={true}
							onClick={() => props.deleteParticipation(part, false)}
							_position="left"
							aria-label="Delete participation"
						/>
					)}

					{(stage?.is_voting ?? false) && (
						<DeleteButton
							squared={true}
							onClick={() => props.deleteParticipation(part, false, true)}
							_position="left"
							aria-label="Delete participation"
						/>
					)}
				</ButtonCollection>
			</td>

			{(display_ocr_detail !== false) && (
				<RequirementsOCRSimple
					data={{
						ocr_data:display_ocr_detail,
						data:{
							ocr_ticket_id:display_ocr_detail,
						},
						user_data:{
							login_id:part.Participation.login_id,
							name:part.Participation.name,
						},
					}}
					stage={{
						Stage:stage,
					}}
					promotion={promotion}
					validation_tags={props.validation_tags}
					close={() => setDisplayOCRDetail(false)}
					img_basedir={props.img_basedir}
					intl={props.intl}
				/>
			)}
		</tr>
	)
};

const UserParticipationsMultistageListBuilder = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	useCSS('comp/quiz.css');

	const [ display_additional_participation, setDisplayAdditionalParticipation ] = useState(false);
	const [ display_participation_details, setDisplayParticipationDetails ] = useState(false);
	const [ display_voting_details, setDisplayVotingDetails ] = useState(false);
	const [ display_team_details, setDisplayTeamDetails ] = useState(false);
	const [ display_voting_stage_id, setDisplayVotingStageID ] = useState(false);
	const [ display_participation_delete_layer, setDisplayParticipationDeleteLayer ] = useState(false);

	const promotion = props?.promotion ?? false;
	const stages = props?.stages ?? false;
	const type = props?.type ?? 'single';	// 'single' | 'grouped_by_stage'

	if(promotion === false || stages === false) return null;

	const display_total = props?.display_total ?? true;
	const display_complete_info = props?.display_complete_info ?? false;
	const display_origin = (display_complete_info) ? (props?.display_origin ?? true) : true;
	const display_prizes = (display_complete_info) ? (props?.display_prizes ?? false) : false;
	const display_date = props?.display_date ?? false;
	const has_game_stages = props?.has_game_stages ?? false;
	const has_quiz_stages = props?.has_quiz_stages ?? false;
	const has_voting_stages = props?.has_voting_stages ?? false;
	const has_team_stages = props?.has_team_stages ?? false;
	const tz = promotion.timezone;

	const participations = props?.stage_participations ?? { summary:[], total:0 };

	const has_ranking_enabled = (promotion && promotion.public_entries != '0') ? true : false;

	const displayDetails = (participation, is_media_content = false) => {
		if(participation.Stage.is_voting) {
			$.get(getRemotedbUrl(`/users/voted_summary/${promotion.id}/${participation.StageScore.stage_id}/${participation.StageScore.id}`), (res) => {
				setDisplayVotingDetails(res);
				setDisplayVotingStageID(participation.StageScore.stage_id);
			}, 'JSON');
			return;
		}
		if(participation.Stage.is_team) {
			setDisplayTeamDetails({
				...participation.StageScore.quiz_entry,
				created:participation.StageScore.created,
				score:participation.StageScore.score,
				stage:{
					teams:participation.Stage.teams,
					id:participation.StageScore.stage_id,
					promotion_id:promotion.id
				},
			});
			return;
		}
		setDisplayParticipationDetails(participation);

		if(is_media_content) return;
		
		if(props.on_view_detail != undefined) {
			
			const n_title = moment.tz(participation.created, local_tz).tz(tz);

			props.on_view_detail(n_title, <SquaredButton
				onClick={() => closeDetails()}
				icon='knk-arrow-l-big'
				_class={{ variable_icon_color:true }}
				aria-label="Close details"
			/>);
		}
	};

	const displayAdditionalInformation = (participation) => {
		setDisplayAdditionalParticipation(participation);
	};

	const deleteParticipation = (participation, confirm, is_voting = false) => {
		if(confirm !== undefined && confirm == true) {
			delMedia(participation, confirm, is_voting, () => {
				if(props.on_delete_participation != undefined) {
					props.on_delete_participation(participation.id);
				}
				setDisplayParticipationDeleteLayer(false);
			});
			return;
		}

		const data = {
			...participation.StageScore,
			promotion_id:promotion.id,
		};
		data.stage = data.stage_id;

		delMedia(data, confirm, is_voting, (response) => {
			setDisplayParticipationDeleteLayer({ ...response, participation:data, is_voting:is_voting });
		});
	};

	let has_score = false;
	if(stages !== false) {
		stages.map((stage) => {
			if(with_score(stage.Stage.promotion_type_id)) has_score = true;
		});
	}

	let media_detail;
	let faceoff_detail;
	let likedislike_detail;
	let guessscore_detail;
	let bracketpredictor_detail;
	if(display_participation_details) {
		const part = display_participation_details;
		const stage_id = part?.Participation?.stage ?? part.StageScore.stage_id;
		const stage_participation = stages.find((stage) => stage.Stage.id == stage_id);
		const qz = part?.StageScore?.quiz_entry ?? {};
		media_detail = qz?.media ?? false;
		faceoff_detail = qz?.faceoff ?? false;
		likedislike_detail = qz?.likedislike ?? false;
		guessscore_detail = (stage_participation.Stage.promotion_type_id == stage_promotion_types.SCORE_PREDICTOR.id) ? qz.guess_score : false;
		bracketpredictor_detail = (stage_participation.Stage.promotion_type_id == stage_promotion_types.BRACKET_PREDICTOR.id) ? qz.guess_score : false;
	}

	return (
		<div>
			<div class="gui_participations_list is_builder">
				{(participations.total != undefined && display_total) && (
					<div>{i18n("Total points")}: {formatNumber(participations.total)}</div>
				)}

				<table class={'single_user_participations' + ((display_total) ? ' gui_not_in_top_big' : '')} cellSpacing="0" cellPadding="0" style={{width:'100%'}}>
					<thead>
						<tr>
							<th style={(display_complete_info) ? { width:'8%' } : null} />

							<th class="gui_text_align_start" style={(!display_complete_info) ? { width:'30%' } : null}>{i18n("Stage")}</th>

							{(display_date) && (
								<th class="gui_text_align_start" style={(display_complete_info) ? { width:'180px' } : null}>{i18n("Date")}</th>
							)}

							{(display_origin) && (
								<th class="gui_text_align_start" style={(display_complete_info) ? { width:'18%' } : null}>{i18n("Origin")}</th>
							)}

							{(has_ranking_enabled || has_score) && (
								<th class="gui_text_align_end" style={(display_complete_info) ? { width:'10%' } : null}>{i18n("score", 'ucfirst')}</th>
							)}

							{(display_complete_info || (has_game_stages || has_quiz_stages)) && (
								<th style={(display_complete_info) ? { width:'18%' } : null} />
							)}
						</tr>
					</thead>

					<tbody>
						{participations.summary.map((part, i) => {
							let display_details = false;
							if(has_game_stages && (part.Stage?.is_game ?? false)) display_details = true;
							if(has_quiz_stages && (part.Stage?.is_quiz ?? false)) display_details = true;
							if(has_voting_stages && (part.Stage?.is_voting ?? false)) display_details = true;
							if(has_team_stages && (part.Stage?.is_team ?? false)) display_details = true;

							return (
								<SingleParticipation
									key={`part_${i}`}
									i={i}
									type={type}
									part={part}
									display_details={display_details}
									display_date={display_date}
									display_origin={display_origin}
									display_prizes={display_prizes}
									display_complete_info={display_complete_info}
									has_ranking_enabled={has_ranking_enabled}
									total_participations={participations.summary.length}
									displayDetails={displayDetails}
									displayAdditionalInformation={displayAdditionalInformation}
									actions={props.actions}
									deleteParticipation={deleteParticipation}
									has_score={has_score}
									in_scores={props.in_scores}
									validation_tags={props.validation_tags}	// only for OCR Ticket
									img_basedir={props.img_basedir}			// only for OCR Ticket
									tz={tz}
									intl={props.intl}
								/>
							)
						})}
					</tbody>
				</table>

				{(display_additional_participation !== false) && (
					<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayAdditionalParticipation(false)}>
						<div class="without_actions">
							<SectionTitle>Additional information in participation</SectionTitle>

							{display_additional_participation.StageParticipation.map((cf, i) => {
								return (
									<div key={`cf_${i}`}>
										{(cf.title != "") && (
											<FieldLabel autotranslate={false}>{cf.title}</FieldLabel>
										)}

										{((cf?.config?.mediatype ?? 'text') == 'image') && (
											<img src={cf.value} width="100%" alt="" />
										)}
									</div>
								)
							})}
						</div>
					</FloatingLayer>
				)}
			</div>

			{(display_participation_details !== false && media_detail) && (
				<ul class="gui_multimedia_gallery multigallery_tool">
					<SingleGallery
						user={{
							Participation:display_participation_details?.Participation ?? {
								...display_participation_details.StageScore,
								name:props.username,
							},
						}}
						selected_entry={true}
						promotion={promotion}
						stage={stages.find((m) => m.Stage.id == display_participation_details.StageScore.stage_id).Stage}
						in_registered={true}
						in_myparticipation={true}
						is_builder={true}
						closeEntryAsLayer={() => setDisplayParticipationDetails(false)}
					/>
				</ul>
			)}

			{(display_participation_details !== false && faceoff_detail) && (
				<FaceOffDetail
					participation={display_participation_details?.Participation ?? {}}
					stages={props.stages}
					backButton={() => setDisplayParticipationDetails(false)}
					intl={props.intl}
				/>
			)}

			{(display_participation_details !== false && bracketpredictor_detail) && (
				<OneTimeBracketDetail
					participation={display_participation_details?.Participation ?? {}}
					promotion={promotion}
					stages={props.stages}
					backButton={() => setDisplayParticipationDetails(false)}
					intl={props.intl}
				/>
			)}

			{(display_participation_details !== false && likedislike_detail) && (
				<LikeDislikeDetail
					participation={display_participation_details?.Participation ?? {}}
					stages={props.stages}
					backButton={() => setDisplayParticipationDetails(false)}
					intl={props.intl}
				/>
			)}

			{(display_participation_details !== false && guessscore_detail) && (
				<GuessScoreDetail
					participation={display_participation_details?.Participation ?? {}}
					promotion={promotion}
					stages={props.stages}
					backButton={() => setDisplayParticipationDetails(false)}
					intl={props.intl}
				/>
			)}

			{(display_participation_details !== false && !media_detail && !faceoff_detail && !likedislike_detail && !guessscore_detail && !bracketpredictor_detail) && (
				<ParticipationDetailLayer
					participation={display_participation_details}
					stages={props.stages}
					backButton={() => setDisplayParticipationDetails(false)}
					tz={tz}
					type={type}
					intl={props.intl}
					in_scores = {props?.in_scores ?? false}
				/>
			)}

			{(display_voting_details !== false) && (
				<FloatingLayer _class={{size:'small'}} backButton={() => setDisplayVotingDetails(false)}>
					<div class="without_actions">
						<MultiEntriesRanking
							promotion={promotion}
							participants={display_voting_details.data}
							autoload_data={false}
							stages={stages}
							stage_id={display_voting_stage_id}
							in_layer={true}
							is_logged={false}
							is_builder={true}
							avail_participations={false}
						/>
					</div>
				</FloatingLayer>
			)}

			{(display_team_details !== false) && (
				<FloatingLayer backButton={() => setDisplayTeamDetails(false)}>
					<div class="without_actions">
						<SectionTitle>Participation detail</SectionTitle>

						<SectionSubtitle
							autotranslate={false}
						>{formatDate(display_team_details, tz)}</SectionSubtitle>

						<LeveledHTMLText
							_class={{ other:'gui_participations_list' }}
						>
							<section class="gui_quiz_public_stats" style={{marginBottom:'2em'}}>
								<article class="gui_flexbox_auto gui_flex_col gui_flex_align_top">
									<p class="question_text">
										{i18n("Score")}: <strong>{display_team_details.score} {i18n("points")}</strong>
									</p>
								</article>
							</section>
						</LeveledHTMLText>

						<UserTeamParticipationDetail
							{...display_team_details}
							display_type='audience'
						/>
					</div>
				</FloatingLayer>
			)}

			{(display_participation_delete_layer !== false) && (
				<DeleteUserConfirm
					response={display_participation_delete_layer}
					confirm={() => deleteParticipation(display_participation_delete_layer.participation, true, display_participation_delete_layer.is_voting)}
					is_voting={display_participation_delete_layer.is_voting}
					backButton={() => setDisplayParticipationDeleteLayer(false)}
				/>
			)}
		</div>
	);
}

export default injectIntl(UserParticipationsMultistageListBuilder);